import { Box, Stack, Typography } from "@mui/material";
import { useErrorHandler } from "@smartrr/shared/components/ErrorBoundary";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { SMARTRR_CUSTOM_ATTRIBUTES_VALUE } from "@smartrr/shared/constants";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { IPurchasable } from "@smartrr/shared/entities/Purchasable";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { IPurchaseStateLineItem } from "@smartrr/shared/entities/PurchaseState/CustomerPurchaseLineItem";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { ICustomAttributesItems, ISmartrrInfoCustomAttributes } from "@smartrr/shared/utils/bundles";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import _ from "lodash";
import React from "react";

import { openModal } from "@customer-app/state/actionCreators/modal";
import {
  VariantPurchaseOptions,
  useVariantEnablementForSellingPlanId,
} from "@customer-app/state/reducers/sellingPlanGroups";
import { useSmartrrCustomerDispatch } from "@customer-app/state/typedCustomerReduxHooks";

import { OrderItemImage } from "../../../constants";
import { ItemPricing } from "../../../Item";
import { getDeliveryId } from "@customer-app/state/stores/delivery/DeliveryId";

interface IBundleItemProps {
  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>;
  item: IPurchaseStateLineItem;
  product: IPurchasable;
  returnToModal: () => void;
  disabled: boolean;
  enabledVariantOptions: VariantPurchaseOptions[];
}

export const BundleItem = ({
  delivery,
  item,
  product,
  returnToModal,
  disabled,
  enabledVariantOptions,
}: IBundleItemProps): JSX.Element => {
  const dispatch = useSmartrrCustomerDispatch();
  const errorHandler = useErrorHandler();

  const { purchaseState } = delivery;
  const { sellingPlanId } = purchaseState;
  const bundleEnabledVariantOptions = useVariantEnablementForSellingPlanId(product!, sellingPlanId, true, true);

  if (enabledVariantOptions.length === 0) {
    enabledVariantOptions = bundleEnabledVariantOptions;
  }

  const customAttributes = item?.customAttributes?.find(ele => ele.key === SMARTRR_CUSTOM_ATTRIBUTES_VALUE);

  if (!customAttributes) {
    errorHandler(new Error(`customAttributes is not defined.\nItem: ${item.id}`));
    return <React.Fragment />;
  }

  const smartrrCustomAttributes = customAttributes?.value;
  let qty = 0;
  const smartrrCustomAttributesParsed: ISmartrrInfoCustomAttributes = JSON.parse(smartrrCustomAttributes!);
  smartrrCustomAttributesParsed.items.forEach((item: ICustomAttributesItems) => (qty += +item.quantity));

  return (
    <Box
      sx={{
        ...(disabled && {
          opacity: ".6",
        }),
      }}
    >
      <OrderItemImage
        purchasable={product}
        quantity={0}
        purchasableVariant={item?.vnt ?? undefined}
        size="medium"
      />
      {disabled ? null : (
        <AquariusButton
          sx={{ margin: "7px 0" }}
          onClick={() =>
            dispatch(
              openModal("EDIT_BUNDLE", {
                deliveryId: getDeliveryId(delivery),
                bundleParentItem: item,
                returnToModal,
              })
            )
          }
        >
          {typedLocalize("editBundle.edit")}
        </AquariusButton>
      )}
      <Stack spacing="3px" direction="column">
        <Typography
          variant="body1"
          className={prefixClassName("bundle-product-name")}
          sx={{ marginBottom: "0 !important" }}
        >
          {_.truncate(product.purchasableName ?? "")}
        </Typography>
        <Typography variant="caption1">
          {typedLocalize("editBundle.packQty", {
            qty,
          })}
        </Typography>
        <Stack direction="row">
          <ItemPricing item={item} delivery={delivery} enabledVariantOptions={enabledVariantOptions} />
        </Stack>
      </Stack>
    </Box>
  );
};
